import { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { ShoppingCart, Store, Trash2, Minus, Plus, ArrowRight, LogIn } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { guestCartItems, setGuestQty, removeGuestItem } from "@/lib/guestCart";
import { Logo } from "@/components/Logo";
import ShopHeader from "@/components/ShopHeader";
import { toast } from "sonner";

export default function GuestCartPage() {
  const nav = useNavigate();
  const { user, booting } = useAuth();
  const [rows, setRows] = useState(null);

  const build = async () => {
    const items = guestCartItems();
    const out = [];
    for (const it of items) {
      try {
        const r = await api.get(`/marketplace/public/product/${it.product_id}`);
        const p = r.data.product;
        const v = it.variant_vid && (p.variants || []).find((x) => x.vid === it.variant_vid);
        out.push({ ...it, name: p.name, slug: p.slug, price: v ? v.price : p.price, image: (v && v.image) || p.image || (p.images || [])[0], variant_label: v ? (v.label || Object.values(v.attributes || {}).join(" / ")) : null });
      } catch { out.push({ ...it, name: "Item unavailable", price: 0, image: null, variant_label: null }); }
    }
    setRows(out);
  };
  useEffect(() => { if (!user) build(); /* eslint-disable-next-line */ }, [user]);

  if (booting || user === undefined) return null;
  if (user) return <Navigate to="/app/cart" replace />;
  if (rows === null) return null;

  const subtotal = rows.reduce((s, r) => s + (r.price || 0) * r.qty, 0);
  const count = rows.reduce((s, r) => s + r.qty, 0);
  const changeQty = (r, q) => { if (q < 1) return; setGuestQty(r.product_id, r.variant_vid, q); build(); };
  const remove = (r) => { removeGuestItem(r.product_id, r.variant_vid); build(); toast.success("Removed"); };
  const toLogin = () => nav(`/login?next=${encodeURIComponent("/app/cart")}`);

  return (
    <div className="min-h-screen bg-slate-50">
      <ShopHeader />
      <div className="mx-auto max-w-5xl px-4 py-6" data-testid="guest-cart-page">
        <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900"><ShoppingCart className="h-5 w-5 text-blue-500" /> Your Cart</h1>
        <p className="mt-1 text-sm text-slate-500">You're browsing as a guest — log in to check out. Your items will carry over to your account.</p>
        {rows.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="guest-cart-empty">
            <ShoppingCart className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-700">Your cart is empty</p>
            <button onClick={() => nav("/shop")} className="mt-4 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white">Browse products</button>
          </div>
        ) : (
          <div className="mt-5 grid gap-5 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
              {rows.map((r) => (
                <div key={`${r.product_id}:${r.variant_vid || ""}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3" data-testid={`guest-cart-item-${r.product_id}`}>
                  <Link to={`/product/${r.slug || r.product_id}`} className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-50">
                    {r.image ? <img src={r.image} alt={r.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Store className="h-6 w-6 text-slate-300" /></div>}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm font-medium text-slate-800">{r.name}</p>
                    {r.variant_label && <p className="text-xs text-slate-400">Variant: {r.variant_label}</p>}
                    <p className="text-sm font-bold text-slate-900">₹{r.price}</p>
                  </div>
                  <div className="flex items-center gap-1 rounded-full border border-slate-200">
                    <button onClick={() => changeQty(r, r.qty - 1)} data-testid={`guest-qty-dec-${r.product_id}`} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-6 text-center text-sm font-semibold" data-testid={`guest-qty-${r.product_id}`}>{r.qty}</span>
                    <button onClick={() => changeQty(r, r.qty + 1)} data-testid={`guest-qty-inc-${r.product_id}`} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <button onClick={() => remove(r)} data-testid={`guest-remove-${r.product_id}`} className="rounded-full p-2 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="h-fit rounded-2xl border border-slate-200 bg-white p-5" data-testid="guest-cart-summary">
              <h3 className="font-semibold text-slate-800">Order Summary</h3>
              <div className="mt-3 flex justify-between text-sm text-slate-600"><span>Items ({count})</span><span>₹{subtotal}</span></div>
              <p className="mt-2 text-xs text-slate-400">Delivery &amp; taxes calculated at checkout.</p>
              <button onClick={toLogin} data-testid="guest-checkout-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full brand-gradient-bg py-3 text-sm font-semibold text-white transition hover:opacity-90">Log in to pay <ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
